// src/components/shared/TableConnections.js
import React from 'react';

/**
 * Draw connection lines between tables that share the same grouping key (default: reserved_id)
 *
 * Props:
 * - tables: array of table objects
 * - groupBy: (table) => key used to group tables (default: t => t.reserved_id)
 * - coord: 'monitor' | 'area' | (table) => ({ x, y }) � where to read positions from
 * - stroke: line color (default 'darkRed')
 * - strokeWidth: number (default 2)
 * - dashArray: optional strokeDasharray string
 */
export default function TableConnections({
  tables = [],
  groupBy = (t) => t.reserved_id,
  coord = 'monitor',
  stroke = 'darkRed',
  strokeWidth = 2,
  dashArray,
}) {
  const center = (t) => {
    if (typeof coord === 'function') return coord(t);
    const useArea = coord === 'area';
    const x = Number(useArea ? t.area_x : t.monitor_x) + Number(t.width) / 2;
    const y = Number(useArea ? t.area_y : t.monitor_y) + Number(t.height) / 2;
    return { x, y };
  };

  const segments = [];
  const groups = {};

  (tables || []).forEach((t) => {
    const key = groupBy(t);
    if (!key) return;
    (groups[key] ||= []).push(t);
  });

  Object.values(groups).forEach((group) => {
    if (group.length < 2) return;
    const sorted = group.slice().sort((a, b) => Number(a.id) - Number(b.id));
    for (let i = 0; i < sorted.length; i++) {
      for (let j = i + 1; j < sorted.length; j++) {
        const a = sorted[i],
          b = sorted[j];
        const ca = center(a),
          cb = center(b);
        segments.push({ x1: ca.x, y1: ca.y, x2: cb.x, y2: cb.y });
      }
    }
  });

  return (
    <svg
      className="table-connections"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    >
      {segments.map((s, i) => (
        <line
          key={i}
          x1={s.x1}
          y1={s.y1}
          x2={s.x2}
          y2={s.y2}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeDasharray={dashArray}
        />
      ))}
    </svg>
  );
}
